// RT-F505 — the shared locale-aware string resolver for AMC's vendored/detached plugin
// webviews. Each isolated plugin bundles its own tsconfig/node_modules and CANNOT import the
// host renderer's i18next instance, so supermail + ollert each hand-copied a BYTE-IDENTICAL
// "real resolver" — hostUiLanguage() + rendererT() with {{name}} interpolation + a CLDR
// Intl.PluralRules plural() — and a locale-correctness fix in one copy silently stayed broken in
// the other. This factory is the single home; each island calls createPluginI18n(itsOwnCatalogs)
// and re-exports the members it uses.
//
// Framework-free leaf by design (like text-fold.ts / relative-time.ts), delivered through
// vendor-sdk so importing it pulls in zero framework code. Behavior is preserved exactly:
// {{name}} double-brace interpolation, CLDR plural category selection (correct for
// Polish/Russian/Arabic — languages with more than two forms), and an activeLocale() seam for
// Intl date/number formatting. Guarded by
// tests/unit/lint/plugin-i18n-resolver-adoption.test.ts (a NEW per-island rendererT +
// Intl.PluralRules re-definition outside this file fails the build).
//
// NOTE the decks resolver (decks/web/src/lib/i18n.ts) is deliberately a DIFFERENT, lesser
// primitive — single-brace {name} interpolation, Record<string, unknown> values, and NO plurals
// (its vendored callers pass {n}) — so it is intentionally NOT folded in here.

export interface PluginI18n {
  /**
   * Resolve a user-facing string: look `key` up in the active-locale catalog, else the English
   * `fallback`, then substitute i18next-style `{{name}}` placeholders from `params`.
   */
  rendererT: (key: string, fallback: string, params?: Record<string, string | number>) => string
  /**
   * The resolved UI locale for this webview (host-injected -> browser -> 'en') — the plugin's
   * active-locale seam for Intl / `toLocale*` date & number formatting, mirroring `rendererT` for
   * strings. Pass it as the first arg (never `undefined`/`'en-US'`) so user-facing dates follow
   * the chosen UI language instead of pinning US English or drifting with the raw host locale.
   */
  activeLocale: () => string
  /**
   * Pick the CLDR plural form for `count`, e.g.
   *   plural(n, { one: '{{count}} filter', other: '{{count}} filters' })
   * Compose with `rendererT(key, plural(n, forms), { count: n })` for word-order-safe
   * interpolation. Falls back to `other` (always required) so a missing category never renders empty.
   */
  plural: (count: number, forms: Partial<Record<Intl.LDMLPluralRule, string>>) => string
}

/**
 * Build a plugin i18n resolver over `catalogs` (locale e.g. 'es' -> { translationKey ->
 * translated string }). Pass `{}` for English-only today — drop a locale in to light it up with
 * no call-site change.
 */
export function createPluginI18n(catalogs: Record<string, Record<string, string>>): PluginI18n {
  // The host injects the resolved UI language as `window.__AMC_LOCALE__` at webview load (plugin
  // locale plumbing). Fall back to the browser locale, then English, so the island still resolves
  // in a bare test / preview context with no host.
  function hostUiLanguage(): string {
    try {
      const injected = (globalThis as { __AMC_LOCALE__?: { uiLanguage?: string } }).__AMC_LOCALE__
      if (injected?.uiLanguage) return injected.uiLanguage
      if (typeof navigator !== 'undefined' && navigator.language) {
        // `?? 'en'` is required, not defensive: these packages set noUncheckedIndexedAccess, so
        // split()[0] is `string | undefined` and the bare index does not compile.
        return navigator.language.split('-')[0] ?? 'en'
      }
    } catch {
      // fall through to English
    }
    return 'en'
  }

  // One shared Intl.PluralRules (runtime/host locale) so count-dependent copy picks its form by
  // CLDR category instead of a hardcoded English `count === 1 ? … : …` — wrong for languages with
  // more than two plural forms (Polish/Russian/Arabic). Construct once (not free per render).
  const PLURAL_RULES = new Intl.PluralRules()

  const rendererT = (
    key: string,
    fallback: string,
    params?: Record<string, string | number>
  ): string => {
    const catalog = catalogs[hostUiLanguage()]
    const template = catalog?.[key] ?? fallback
    if (!params) return template
    // Substitute i18next-style `{{name}}` placeholders so a template like
    // 'Dismissed {{note}}' renders real values instead of English-order concatenation.
    return template.replace(/\{\{(\w+)\}\}/g, (match, name: string) =>
      name in params ? String(params[name]) : match
    )
  }

  const activeLocale = (): string => hostUiLanguage()

  const plural = (count: number, forms: Partial<Record<Intl.LDMLPluralRule, string>>): string =>
    forms[PLURAL_RULES.select(count)] ?? forms.other ?? ''

  return { rendererT, activeLocale, plural }
}
